import { useCallback, useEffect, useState } from "react"

import * as turf from "@turf/turf"
import _ from "lodash"
import { Marker } from "react-map-gl/mapbox"
import { useEffectOnce } from "react-use"

import { PinIcon } from "@l2r-front/l2r-icons"
import { MAP_POINT_EDITOR_ID, useMapDispatchContext, useMapStateContext } from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"

import { useRoadBoundingBox } from "../../hooks"

import * as Styled from "./MapPointEditor.styled"

export const MapPointEditor = (props) => {

    const {
        children,
        onMove,
        onRoadDistanceChanged,
        position,
        referential,
        road,
        subNetworks,
    } = props

    const theme = useTheme()
    const isMobile = useIsMobileDevice()
    const [selectedRoad, setSelectedRoad] = useState(null)
    const roadBoundingBox = useRoadBoundingBox(selectedRoad)
    const { getMapRef, setMapBoundingBoxToFit, storeMapBoundingBox } = useMapDispatchContext()
    const map = getMapRef(MAP_POINT_EDITOR_ID)?.getMap()
    const baseMap = getMapRef()?.getMap()
    const [hasFittedBoundingBox, setHasFittedBoundingBox] = useState(true)
    const [mapBoundingBox, setMapBoundingBox] = useState(null)
    const { storedBoundingBox } = useMapStateContext()
    const mapInitialZoom = baseMap.getZoom() || 18

    const handleMove = useCallback(e => {
        const { longitude, latitude } = e.viewState

        onMove({
            longitude,
            latitude,
        })
    }, [onMove])

    useEffect(() => {
        if (!position || !referential || !onRoadDistanceChanged) {
            return
        }

        const boundingBox = map?.getBounds()
        if (boundingBox) {
            const boundingBoxToStore = {
                latitude: position.latitude,
                longitude: position.longitude,
                zoom: map.getZoom(),
            }
            if (!_.isEqual(mapBoundingBox, boundingBoxToStore)) {
                setMapBoundingBox(boundingBoxToStore)
            }
        }

        const point = turf.point([position.longitude, position.latitude])
        const nearestRoadLinear = referential ? turf.featureReduce(referential,
            (previousValue, currentFeature) => {
                const distance = turf.pointToLineDistance(point, currentFeature.geometry)
                if (distance < previousValue.distance) {
                    return {
                        distance,
                        linearLocation: currentFeature.properties.linearLocation[0],
                    }
                } else {
                    return previousValue
                }
            },
            { linearLocation: {}, distance: Infinity },
        ) : {}
        onRoadDistanceChanged(nearestRoadLinear)
    }, [map, mapBoundingBox, position, referential, onRoadDistanceChanged])

    useEffect(() => {
        if (!hasFittedBoundingBox && roadBoundingBox && selectedRoad) {
            setMapBoundingBoxToFit(roadBoundingBox, undefined, MAP_POINT_EDITOR_ID)
            setHasFittedBoundingBox(true)
        }
        return () => {
            storeMapBoundingBox(MAP_POINT_EDITOR_ID, mapBoundingBox)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasFittedBoundingBox, map, mapBoundingBox, roadBoundingBox, selectedRoad, setMapBoundingBoxToFit])

    useEffectOnce(() => {
        const initialViewState = storedBoundingBox?.[MAP_POINT_EDITOR_ID] ?? {
            longitude: position.longitude,
            latitude: position.latitude,
            zoom: mapInitialZoom,
        }
        onMove(initialViewState)
    }, [])

    return <Styled.MapWithSearchBar
        displayMapLayerSelector={false}
        roadSearchClosed={true}
        displayGpsLocationPin={false}
        id={MAP_POINT_EDITOR_ID}
        initialViewState={storedBoundingBox?.[MAP_POINT_EDITOR_ID] ?? {
            longitude: position.longitude,
            latitude: position.latitude,
            zoom: mapInitialZoom,
        }}
        onMove={handleMove}
        onRoadSelected={(newValue) => {
            setSelectedRoad(newValue)
            setHasFittedBoundingBox(false)
        }}
        subNetworks={subNetworks}
        style={{ borderRadius: theme.spacing(3) }}
    >
        <Styled.TopLeftAnchor isMobile={isMobile}>
            {!isMobile ? true : road ? <Styled.RoadContainer>
                <Styled.RoadText variant="Small" noWrap>
                    {road}
                </Styled.RoadText>
            </Styled.RoadContainer> : null}
        </Styled.TopLeftAnchor>
        <Styled.TopRightAnchor isMobile={isMobile}>
            {isMobile ? true : road ? <Styled.RoadContainer>
                <Styled.RoadText variant="Small" noWrap>
                    {road}
                </Styled.RoadText>
            </Styled.RoadContainer> : null}
        </Styled.TopRightAnchor>
        {children}
        <Marker {...position} anchor="bottom">
            <PinIcon style={{ color: theme.palette["error"].main, transform: "scale(2)" }} />
        </Marker>
    </Styled.MapWithSearchBar>
}

MapPointEditor.propTypes = {
    children: PropTypes.node,
    onMove: PropTypes.func.isRequired,
    onRoadDistanceChanged: PropTypes.func.isRequired,
    position: PropTypes.shape({
        longitude: PropTypes.number,
        latitude: PropTypes.number,
    }).isRequired,
    referential: PropTypes.object,
    road: PropTypes.string,
}
